import React, { useState, useEffect } from "react";
import { useDeal } from "../lib/store";
import { streamPost, emailApi } from "../lib/api";
import { Card } from "../components/shared";
import * as m from "../lib/model";
import { toast } from "sonner";
import { Send, Paperclip, X, FileText, Wand2, Upload, Scissors, Eraser, Mail, Users } from "lucide-react";
import { BrokerFinder } from "../components/brokers/BrokerFinder";

const BUYER_LIST_TEMPLATE = (b, firmName) => `Hi ${((b.name || "").split(" ")[0]) || "there"},

I run acquisitions at ${firmName}. We buy and hold profitable lower-middle-market businesses — $500K to $3M of EBITDA — and close with committed equity plus a bank facility, no financing contingency.

Could you add me to your buyer list${b.specialty ? ` for ${b.specialty}` : ""}? Happy to sign an NDA the same day and give you a same-week read on anything you send.

Thanks,`;

const SENDER = { name: "XYZ Ventures", email: "ike@xyzvtr.com" };
const toB64 = (str) => btoa(unescape(encodeURIComponent(str)));
const stripSubject = (text) => text.replace(/^subject:.*(\r?\n)+/i, "").trim();
const grabSubject = (text, fallback) => {
  const mt = (text || "").match(/subject:\s*(.*)/i);
  return mt ? mt[1].trim() : fallback;
};

export default function BrokerLOI() {
  const { M } = useDeal();
  const [to, setTo] = useState({ name: M.brokerName || "", email: M.brokerEmail || "" });
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [showCc, setShowCc] = useState(false);
  const [subject, setSubject] = useState(`Questions & Indication of Interest — ${M.dealName}`);
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [drafting, setDrafting] = useState(null); // "questions" | "ioi" | body target
  const [shortening, setShortening] = useState(false);
  const [shortenInstruction, setShortenInstruction] = useState("");
  const [attaching, setAttaching] = useState(null); // "loi" | "ioi" | "questions"
  const [sending, setSending] = useState(false);
  const fileRef = React.useRef(null);
  const bodyRef = React.useRef(null);
  const [tab, setTab] = useState("compose");

  // Jump from the broker list into Compose with the buyer-list ask pre-filled (AI draft when supplied).
  const openFromBroker = (b) => {
    setTo({ name: b.name || b.firm || "", email: b.email || "" });
    setSubject(b.subject || `Buyer list — ${M.industry || "lower-middle-market"} acquisitions`);
    setBody(b.body || BUYER_LIST_TEMPLATE(b, SENDER.name));
    setTab("compose");
  };

  useEffect(() => {
    setTo((t) => ({ name: M.brokerName || t.name, email: M.brokerEmail || t.email }));
    setSubject(`Questions & Indication of Interest — ${M.dealName}`);
  }, [M.brokerName, M.brokerEmail, M.dealName]);

  const VARS = [
    ["Deal name", M.dealName],
    ["Asking price", m.fmt$(M.askPrice)],
    ["Adj. SDE", m.fmt$(M.sde)],
    ["Year-1 DSCR", m.fmtX(m.dscrYr1(M))],
    ["Broker", M.brokerName || "[Broker]"],
  ];
  const insertVar = (val) => {
    const el = bodyRef.current;
    if (!el) { setBody((b) => b + val); return; }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    setBody(body.slice(0, start) + val + body.slice(end));
    requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = start + val.length; });
  };

  const draftBody = async (docType) => {
    setDrafting(docType);
    let full = "";
    try {
      await streamPost("/ai/loi", { deal: M, doc_type: docType }, (c, f) => { full = f; setBody(stripSubject(f)); });
      setSubject(grabSubject(full, subject));
      toast.success(docType === "questions" ? "Diligence questions drafted into the email" : "IOI note drafted into the email");
    } catch { toast.error("Draft failed"); }
    finally { setDrafting(null); }
  };

  const shorten = async (instrOverride) => {
    if (!body.trim()) return toast.error("Nothing to rewrite yet — draft or write a message first");
    const instr = (instrOverride !== undefined ? instrOverride : shortenInstruction).trim();
    setShortening(true);
    try {
      await streamPost("/ai/shorten", { text: body, instruction: instr || null }, (c, f) => { setBody(f); });
      toast.success(instr ? "Rewritten to your instruction" : "Shortened to a tighter version");
    } catch { toast.error("Could not rewrite"); }
    finally { setShortening(false); }
  };

  const generateAttachment = async (docType) => {
    setAttaching(docType);
    let full = "";
    try {
      await streamPost("/ai/loi", { deal: M, doc_type: docType }, (c, f) => { full = f; });
      const label = docType === "loi" ? "Letter of Intent" : docType === "ioi" ? "Indication of Interest" : "Diligence Questions";
      const filename = `${label.replace(/\s+/g, "-")}-${M.dealName.replace(/\s+/g, "-")}.txt`;
      setAttachments((a) => [...a.filter((x) => x.filename !== filename), { filename, content: toB64(full), label, size: full.length }]);
      toast.success(`Attached ${label} (${filename})`);
    } catch { toast.error("Could not generate attachment"); }
    finally { setAttaching(null); }
  };

  const onUpload = (file) => {
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = String(reader.result).split(",")[1] || "";
      setAttachments((a) => [...a, { filename: file.name, content: b64, label: file.name, size: file.size }]);
      toast.success(`Attached ${file.name}`);
    };
    reader.readAsDataURL(file);
  };

  const removeAttachment = (fn) => setAttachments((a) => a.filter((x) => x.filename !== fn));

  const send = async () => {
    if (!to.email) return toast.error("Enter the broker's email in the To field");
    if (!subject.trim()) return toast.error("Add a subject");
    if (!body.trim() && !attachments.length) return toast.error("Write a message or attach a document");
    setSending(true);
    try {
      const res = await emailApi.compose({
        to: [{ name: to.name, email: to.email }],
        cc: cc.split(/[,;\s]+/).filter(Boolean),
        bcc: bcc.split(/[,;\s]+/).filter(Boolean),
        subject,
        body,
        attachments: attachments.map(({ filename, content }) => ({ filename, content })),
        deal_name: M.dealName,
        kind: "broker",
      });
      if (res.mock) toast.warning(`Recorded — no live email sent (sender config)`);
      else toast.success(`Email sent to ${to.email}${attachments.length ? ` with ${attachments.length} attachment(s)` : ""} via Resend`);
    } catch { toast.error("Send failed"); }
    finally { setSending(false); }
  };

  return (
    <div className="page" data-testid="page-loi">
      <div className="page-head">
        <div className="page-title">Broker Outreach</div>
        <div className="page-sub">Email the broker your questions and attach a drafted LOI / IOI — or find new brokers and ask to join their buyer list</div>
      </div>

      <div className="sub-tabs" data-testid="loi-tabs">
        <button className={`sub-tab ${tab === "compose" ? "on" : ""}`} onClick={() => setTab("compose")} data-testid="loi-tab-compose"><Mail size={13} />Compose</button>
        <button className={`sub-tab ${tab === "find" ? "on" : ""}`} onClick={() => setTab("find")} data-testid="loi-tab-find"><Users size={13} />Find brokers</button>
      </div>

      {tab === "find" && <BrokerFinder onEmail={openFromBroker} />}

      <div className="grid g2" style={{ alignItems: "start", display: tab === "compose" ? undefined : "none" }}>
        <Card title="Compose Email" testid="broker-compose">
          {/* From */}
          <div className="cmp-row">
            <span className="cmp-k">From</span>
            <span className="cmp-from" data-testid="compose-from">{SENDER.name} <span className="mono" style={{ color: "var(--text3)" }}>{SENDER.email}</span></span>
          </div>
          {/* To */}
          <div className="cmp-row">
            <span className="cmp-k">To</span>
            <div style={{ display: "flex", gap: 8, flex: 1 }}>
              <input className="inp" style={{ flex: "0 0 40%" }} placeholder="Broker name" value={to.name} onChange={(e) => setTo({ ...to, name: e.target.value })} data-testid="compose-to-name" />
              <input className="inp" style={{ flex: 1 }} placeholder="broker@firm.com" value={to.email} onChange={(e) => setTo({ ...to, email: e.target.value })} data-testid="compose-to-email" />
              <button className="btn" onClick={() => setShowCc((s) => !s)} data-testid="compose-cc-toggle">Cc/Bcc</button>
            </div>
          </div>
          {showCc && (
            <>
              <div className="cmp-row"><span className="cmp-k">Cc</span><input className="inp" style={{ flex: 1 }} placeholder="comma-separated" value={cc} onChange={(e) => setCc(e.target.value)} data-testid="compose-cc" /></div>
              <div className="cmp-row"><span className="cmp-k">Bcc</span><input className="inp" style={{ flex: 1 }} placeholder="comma-separated" value={bcc} onChange={(e) => setBcc(e.target.value)} data-testid="compose-bcc" /></div>
            </>
          )}
          {/* Subject */}
          <div className="cmp-row">
            <span className="cmp-k">Subject</span>
            <input className="inp inp-text" style={{ flex: 1 }} value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="compose-subject" />
          </div>

          {/* Draft helpers */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "10px 0 6px" }}>
            <button className="btn" onClick={() => draftBody("questions")} disabled={drafting === "questions"} data-testid="draft-questions"><Wand2 size={13} style={{ marginRight: 4 }} />{drafting === "questions" ? "Drafting…" : "Draft Questions"}</button>
            <button className="btn" onClick={() => draftBody("ioi")} disabled={drafting === "ioi"} data-testid="draft-ioi-body"><Wand2 size={13} style={{ marginRight: 4 }} />{drafting === "ioi" ? "Drafting…" : "Draft IOI Note"}</button>
            <button className="btn" onClick={() => shorten("")} disabled={shortening || !body.trim()} data-testid="shorten-email"><Scissors size={13} style={{ marginRight: 4 }} />{shortening ? "Working…" : "Shorten"}</button>
            <button className="btn" onClick={() => setBody("")} disabled={!body.trim()} data-testid="clear-email"><Eraser size={13} style={{ marginRight: 4 }} />Clear</button>
          </div>
          {/* AI rewrite instruction */}
          <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 8 }}>
            <Wand2 size={13} style={{ color: "var(--text3)", flex: "0 0 auto" }} />
            <input className="inp" style={{ flex: 1 }} placeholder={'AI rewrite — e.g. "shorten to 3 sentences" or "only ask about timing"'} value={shortenInstruction} onChange={(e) => setShortenInstruction(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); shorten(); } }} data-testid="shorten-instruction" />
            <button className="btn btn-primary" onClick={() => shorten()} disabled={shortening || !body.trim() || !shortenInstruction.trim()} data-testid="apply-instruction">{shortening ? "…" : "Apply"}</button>
          </div>

          {/* Body */}
          <textarea ref={bodyRef} className="inp" rows={12} style={{ resize: "vertical", fontFamily: "inherit" }} placeholder="Hi [Broker], a few quick questions on this opportunity… (or click Draft Questions)" value={body} onChange={(e) => setBody(e.target.value)} data-testid="compose-body" />

          {/* Insert variables */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8, alignItems: "center" }}>
            <span style={{ fontSize: 10.5, color: "var(--text3)" }}>Insert:</span>
            {VARS.map(([label, val]) => (
              <button key={label} className="chip" onClick={() => insertVar(String(val))} data-testid={`var-${label.replace(/\s+/g, "-").toLowerCase()}`}>{label}</button>
            ))}
          </div>

          {/* Attachments */}
          <div className="section-label" style={{ marginTop: 14 }}>Attachments</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
            <button className="btn btn-primary" onClick={() => generateAttachment("loi")} disabled={attaching === "loi"} data-testid="attach-loi"><FileText size={13} style={{ marginRight: 4 }} />{attaching === "loi" ? "Drafting LOI…" : "Generate & Attach LOI"}</button>
            <button className="btn" onClick={() => generateAttachment("ioi")} disabled={attaching === "ioi"} data-testid="attach-ioi">{attaching === "ioi" ? "Drafting…" : "Attach IOI"}</button>
            <button className="btn" onClick={() => generateAttachment("questions")} disabled={attaching === "questions"} data-testid="attach-questions-file">{attaching === "questions" ? "Drafting…" : "Attach Questions"}</button>
            <input ref={fileRef} type="file" style={{ display: "none" }} onChange={(e) => e.target.files[0] && onUpload(e.target.files[0])} data-testid="attach-file-input" />
            <button className="btn" onClick={() => fileRef.current?.click()} data-testid="attach-upload"><Upload size={13} style={{ marginRight: 4 }} />Upload File</button>
          </div>
          {attachments.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {attachments.map((a) => (
                <div key={a.filename} className="attach-chip" data-testid="attach-chip">
                  <Paperclip size={13} />
                  <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.filename}</span>
                  <span style={{ color: "var(--text3)", fontSize: 10.5 }}>{(a.size / 1024).toFixed(1)} KB</span>
                  <button className="btn" style={{ padding: "2px 6px" }} onClick={() => removeAttachment(a.filename)} data-testid="attach-remove"><X size={13} /></button>
                </div>
              ))}
            </div>
          ) : <div style={{ fontSize: 11, color: "var(--text3)" }}>No attachments. Draft an LOI/IOI as a file, or upload your own.</div>}

          <button className="btn btn-primary" style={{ width: "100%", marginTop: 14 }} onClick={send} disabled={sending} data-testid="compose-send">
            <Send size={13} style={{ marginRight: 6 }} />{sending ? "Sending…" : "Send Email"}
          </button>
          <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 8 }}>Sent live via Resend from {SENDER.email}. Attachments are delivered as files, not pasted into the message.</div>
        </Card>

        {/* Live preview */}
        <Card title="Preview" testid="compose-preview">
          <div className="email-preview" data-testid="email-preview">
            <div className="ep-head">
              <div><span className="ep-k">From</span> {SENDER.name} &lt;{SENDER.email}&gt;</div>
              <div><span className="ep-k">To</span> {to.name ? `${to.name} <${to.email || "…"}>` : (to.email || "…")}</div>
              {cc && <div><span className="ep-k">Cc</span> {cc}</div>}
              <div><span className="ep-k">Subject</span> <b>{subject || "(no subject)"}</b></div>
            </div>
            <div className="ep-body">{body || <span style={{ color: "var(--text3)" }}>Your message will appear here…</span>}</div>
            {attachments.length > 0 && (
              <div className="ep-attach">
                {attachments.map((a) => <span key={a.filename} className="attach-chip"><Paperclip size={12} />{a.filename}</span>)}
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
